import { Koi } from '@ksyos/koi';
import type { Section } from '@common/formEngine/formEngine.types';
import type { IGeneralPractitioner } from '@common/interfaces';
import type { DeepPickPath } from '@common/utils/deepPickUtils';
import type { NullablePrimitives } from '@common/utils/utilityTypes';
import type { ProcessActivityAsset } from '@common/validation/processActivityAsset';
import { AssetCategory } from '@common/enums';
import { dependsOn, valueDependsOn } from '@common/formEngine/dependsOn';
import { getGeneralPractitionerSchema } from '@common/validation/customControls/generalPractitioner';
import {
    getEmptyProcessActivityAssetCategorySchema,
    getProcessActivityAssetsSchema,
} from '@common/validation/processActivityAsset';
import { emailSchema } from '@common/validation/schemas';
import { getTodayOrBeforeDateSchema } from '@common/validation/utils/dateSchemas';
import { getTextInputStringSchema } from '@common/validation/utils/getTextInputStringSchema';
import type { OorZijde, Patient } from '../utils';
import { oorZijdeOptions, RedenVerwijzingAudioloog, Slechthorendheid, slechthorendheidOptions } from '../utils';

export enum VerwijsNaar {
    audioloog = 'Audioloog',
    knoArts = 'KNO-arts',
}
export const verwijsNaarOptions: VerwijsNaar[] = [VerwijsNaar.knoArts, VerwijsNaar.audioloog];

export enum RedenVerwijzingKnoArts {
    anders = 'Anders',
    asymmetrie = 'Asymmetrie',
    beoordelingSoloapparatuur = 'Beoordeling soloapparatuur',
    beoordelingWekEnWaarschuwingssysteem = 'Beoordeling wek- en waarschuwingssysteem',
    discrepantieToonSpraakAudiogram = 'Discrepantie tussen toon- en spraakaudiogram',
    jongerDan67Jaar = 'Jonger dan 67 jaar',
    significantVerslechterdGehoor = 'Significant verslechterd gehoor',
    slechteSpraakDiscriminatie = 'Slechte spraak discriminatie',
}
const redenVerwijzingKnoArtsOptions: RedenVerwijzingKnoArts[] = [
    RedenVerwijzingKnoArts.jongerDan67Jaar,
    RedenVerwijzingKnoArts.asymmetrie,
    RedenVerwijzingKnoArts.discrepantieToonSpraakAudiogram,
    RedenVerwijzingKnoArts.slechteSpraakDiscriminatie,
    RedenVerwijzingKnoArts.significantVerslechterdGehoor,
    RedenVerwijzingKnoArts.beoordelingSoloapparatuur,
    RedenVerwijzingKnoArts.beoordelingWekEnWaarschuwingssysteem,
    RedenVerwijzingKnoArts.anders,
];

const redenVerwijzingAudioloogOptions: RedenVerwijzingAudioloog[] = [
    RedenVerwijzingAudioloog.gesprekVanwegeTinnitus,
    RedenVerwijzingAudioloog.hoortoestelAdviesVoorProef,
    RedenVerwijzingAudioloog.hoortoestelAdviesNaProef,
    RedenVerwijzingAudioloog.adviesOverigeHulpmiddelen,
    RedenVerwijzingAudioloog.anders,
];
export type RedenVerwijzing = RedenVerwijzingKnoArts | RedenVerwijzingAudioloog;

export enum HuidigHoorhulpmiddel {
    anders = 'Anders, namelijk...',
    hoortoestelLinks = 'Hoortoestel links',
    hoortoestelRechts = 'Hoortoestel rechts',
    soloapparatuur = 'Soloapparatuur',
    wekEnWaarschuwingssysteem = 'Wek- en waarschuwingssysteem',
}
const huidigHoorhulpmiddelOptions: HuidigHoorhulpmiddel[] = [
    HuidigHoorhulpmiddel.hoortoestelLinks,
    HuidigHoorhulpmiddel.hoortoestelRechts,
    HuidigHoorhulpmiddel.soloapparatuur,
    HuidigHoorhulpmiddel.wekEnWaarschuwingssysteem,
    HuidigHoorhulpmiddel.anders,
];

export enum AardVanSlechthorendheid {
    conductief = 'Conductief',
    gemengd = 'Gemengd',
    perceptief = 'Perceptief',
}
const aardVanSlechthorendheidOptions: AardVanSlechthorendheid[] = [
    AardVanSlechthorendheid.perceptief,
    AardVanSlechthorendheid.conductief,
    AardVanSlechthorendheid.gemengd,
];

export enum Duizeligheid {
    draaiduizeligheid = 'Draaiduizeligheid',
    gevoelFlauwTeVallen = 'Gevoel flauw te vallen',
    lichtGevoelInHetHoofd = 'Licht gevoel in het hoofd',
    onzekerheidBijBewegen = 'Onzekerheid bij bewegen',
}
const duizeligheidOptions: Duizeligheid[] = [
    Duizeligheid.draaiduizeligheid,
    Duizeligheid.lichtGevoelInHetHoofd,
    Duizeligheid.gevoelFlauwTeVallen,
    Duizeligheid.onzekerheidBijBewegen,
];
export enum OorsuizenSoort {
    continu = 'Continu',
    hartsynchroon = 'Hartsynchroon',
}
const oorsuizenSoortOptions: OorsuizenSoort[] = [OorsuizenSoort.continu, OorsuizenSoort.hartsynchroon];

export type HearingLossExaminationDataV2 = NullablePrimitives<{
    aard_van_slechthorendheid: string;
    andere_relevante_voorgeschiedenis: boolean;
    andere_relevante_voorgeschiedenis_toelichting: string;
    assets: ProcessActivityAsset[];
    audiometrie: never[];
    duizeligheid: boolean;
    duizeligheid_soort: string;
    duizeligheid_toelichting: string;
    generalPractitioner: IGeneralPractitioner;
    hersenvliesontsteking: boolean;
    hersenvliesontsteking_momenteel_klachten: boolean;
    hersenvliesontsteking_momenteel_klachten_toelichting: string;
    hersenvliesontsteking_reeds_bezochte_zorgverlener: string;
    hersenvliesontsteking_toelichting: string;
    hersenvliesontsteking_wanneer: string;
    hoofdtrauma: boolean;
    hoofdtrauma_begin: Date | string;
    hoofdtrauma_momenteel_klachten: boolean;
    hoofdtrauma_momenteel_klachten_toelichting: string;
    hoofdtrauma_reeds_bezochte_zorgverlener: string;
    hoofdtrauma_soort: string;
    hoofdtrauma_toelichting: string;
    hoorhulpmiddel: boolean;
    huidig_hoorhulpmiddel: NullablePrimitives<{
        anders: string;
        hoortoestelLinks: boolean;
        hoortoestelRechts: boolean;
        soloapparatuur: boolean;
        wekEnWaarschuwingssysteem: boolean;
    }>;
    huidig_hoorhulpmiddel_leeftijd: string;
    huidig_hoorhulpmiddel_merk_en_type: string;
    huidige_hoortoestelinstellingen: never[];
    motivatie_patient: string;
    oorontsteking: boolean;
    oorontsteking_begin: string | Date;
    oorontsteking_chronisch: boolean;
    oorontsteking_toelichting: string;
    oorontsteking_zijde: OorZijde;
    ooroperatie: boolean;
    ooroperatie_complicaties: boolean;
    ooroperatie_complicaties_momenteel_klachten: boolean;
    ooroperatie_complicaties_momenteel_klachten_toelichting: string;
    ooroperatie_complicaties_toelichting: string;
    ooroperatie_datum: string | Date;
    ooroperatie_toelichting: string;
    ooroperatie_type: string;
    ooroperatie_zijde: OorZijde;
    oorpijn: boolean;
    oorpijn_begin: string | Date;
    oorpijn_toelichting: string;
    oorpijn_zijde: OorZijde;
    oorsmeer_regelmatig_reinigen: boolean;
    oorsmeer_toelichting: string;
    oorsmeer_veel: boolean;
    oorsmeer_zijde: OorZijde;
    oorsuizen: boolean;
    oorsuizen_begin: string | Date;
    oorsuizen_beschrijving: string;
    oorsuizen_soort: OorsuizenSoort;
    oorsuizen_toelichting: string;
    oorsuizen_zijde: OorZijde;
    overige_klachten: boolean;
    overige_klachten_toelichting: string;
    overmatige_blootstelling_aan_lawaai: boolean;
    overmatige_blootstelling_aan_lawaai_oorzaak: string;
    overmatige_blootstelling_aan_lawaai_periode: string;
    overmatige_blootstelling_aan_lawaai_toelichting: string;
    patient: Patient;
    reden_verwijzing: RedenVerwijzing;
    reden_verwijzing_anders: string;
    rem_meting_en_vrije_veld_spraakaudiometrie: never[];
    slechthorendheid: Slechthorendheid;
    slechthorendheid_in_familie: boolean;
    slechthorendheid_in_familie_toelichting: string;
    toestemming_van_patient: boolean;
    trommelvliesfoto: never[];
    verwijs_naar: VerwijsNaar;
    vraag_aan_specialist: string;
}>;

export type HearingLossExaminationKeyV2 = DeepPickPath<HearingLossExaminationDataV2>;

export const hearingLossExaminationSectionsV2: Section<HearingLossExaminationDataV2>[] = [
    {
        fields: [
            {
                draftSchema: Koi.boolean().allow(null),
                finalSchema: Koi.boolean().valid(true),
                initialValue: null,
                isRequired: true,
                key: 'toestemming_van_patient',
                label: 'Toestemming van patiënt',
                type: 'custom',
            },
        ],
    },
    {
        fields: [
            {
                draftSchema: getTextInputStringSchema(true, 255),
                finalSchema: getTextInputStringSchema(false, 255),
                isRequired: true,
                key: 'patient.mobileNumber',
                label: 'Telefoonnummer',
                type: 'textInput',
            },
            {
                draftSchema: getTextInputStringSchema(true, 255),
                finalSchema: emailSchema,
                isRequired: true,
                key: 'patient.emailAddress',
                label: 'E-mailadres',
                type: 'textInput',
            },
            {
                draftSchema: getGeneralPractitionerSchema(true),
                finalSchema: getGeneralPractitionerSchema(),
                initialValue: {
                    organizationId: null,
                    practitionerId: null,
                },
                isRequired: true,
                key: 'generalPractitioner',
                label: 'Huisarts van de patiënt',
                type: 'custom',
            },
        ],
        title: 'Gegevens patiënt',
    },
    {
        fields: [
            {
                isRequired: true,
                key: 'verwijs_naar',
                label: 'Verwijs naar',
                type: 'radioGroup',
                valueOptions: verwijsNaarOptions,
            },
            {
                draftSchema: Koi.when('verwijs_naar', {
                    is: VerwijsNaar.knoArts,
                    otherwise: Koi.koi().enum(RedenVerwijzingAudioloog).allow(null),
                    then: Koi.koi().enum(RedenVerwijzingKnoArts).allow(null),
                }),
                finalSchema: Koi.when('verwijs_naar', {
                    is: VerwijsNaar.knoArts,
                    otherwise: Koi.koi().enum(RedenVerwijzingAudioloog),
                    then: Koi.koi().enum(RedenVerwijzingKnoArts),
                }),
                include: dependsOn('verwijs_naar', ({ verwijs_naar }) => verwijs_naar !== null),
                isRequired: true,
                key: 'reden_verwijzing',
                label: 'Reden van verwijzing',
                type: 'select',
                valueOptions: valueDependsOn('verwijs_naar', ({ verwijs_naar }) =>
                    verwijs_naar === VerwijsNaar.knoArts
                        ? redenVerwijzingKnoArtsOptions
                        : redenVerwijzingAudioloogOptions
                ),
            },
            {
                include: dependsOn('reden_verwijzing', ({ reden_verwijzing }) => reden_verwijzing === 'Anders'),
                isRequired: true,
                key: 'reden_verwijzing_anders',
                label: 'Reden anders',
                type: 'textArea',
            },
            {
                isRequired: dependsOn(
                    'reden_verwijzing',
                    ({ reden_verwijzing }) =>
                        reden_verwijzing !== null &&
                        [
                            RedenVerwijzingKnoArts.beoordelingWekEnWaarschuwingssysteem,
                            RedenVerwijzingKnoArts.beoordelingSoloapparatuur,
                            'Anders',
                        ].includes(reden_verwijzing)
                ),
                key: 'motivatie_patient',
                label: 'Motivatie van de patiënt',
                type: 'textArea',
            },
            {
                isRequired: true,
                key: 'hoorhulpmiddel',
                label: 'Hoorhulpmiddel',
                type: 'boolean',
            },
            {
                checkboxEnumType: HuidigHoorhulpmiddel,
                include: dependsOn('hoorhulpmiddel'),
                isRequired: true,
                key: 'huidig_hoorhulpmiddel',
                label: 'Huidig hoorhulpmiddel',
                type: 'checkboxGroup',
                valueOptions: huidigHoorhulpmiddelOptions,
            },
            {
                include: dependsOn(
                    ['huidig_hoorhulpmiddel', 'verwijs_naar'],
                    ({ huidig_hoorhulpmiddel, verwijs_naar }) =>
                        verwijs_naar === VerwijsNaar.audioloog &&
                        (huidig_hoorhulpmiddel.hoortoestelLinks ||
                            huidig_hoorhulpmiddel.hoortoestelRechts ||
                            huidig_hoorhulpmiddel.soloapparatuur ||
                            huidig_hoorhulpmiddel.wekEnWaarschuwingssysteem ||
                            !!huidig_hoorhulpmiddel.anders)
                ),
                isRequired: true,
                key: 'huidig_hoorhulpmiddel_merk_en_type',
                label: 'Merk en type huidig hoorhulpmiddel',
                type: 'textInput',
            },
            {
                include: dependsOn(
                    'huidig_hoorhulpmiddel',
                    ({ huidig_hoorhulpmiddel }) =>
                        huidig_hoorhulpmiddel.hoortoestelLinks ||
                        huidig_hoorhulpmiddel.hoortoestelRechts ||
                        huidig_hoorhulpmiddel.soloapparatuur ||
                        huidig_hoorhulpmiddel.wekEnWaarschuwingssysteem ||
                        !!huidig_hoorhulpmiddel.anders
                ),
                isRequired: true,
                key: 'huidig_hoorhulpmiddel_leeftijd',
                label: 'Leeftijd hoorhulpmiddel',
                type: 'textInput',
            },
            {
                isRequired: true,
                key: 'slechthorendheid',
                label: 'Slechthorendheid',
                type: 'radioGroup',
                valueOptions: slechthorendheidOptions,
            },
            {
                allowOtherOption: true,
                include: dependsOn(
                    ['verwijs_naar', 'slechthorendheid'],
                    ({ slechthorendheid, verwijs_naar }) =>
                        verwijs_naar === VerwijsNaar.audioloog &&
                        slechthorendheid !== null &&
                        slechthorendheid !== Slechthorendheid.geen
                ),
                isRequired: true,
                key: 'aard_van_slechthorendheid',
                label: 'Aard van slechthorendheid',
                type: 'radioGroup',
                valueOptions: aardVanSlechthorendheidOptions,
            },
        ],
        subsections: [
            {
                fields: [
                    {
                        isRequired: true,
                        key: 'duizeligheid',
                        label: 'Duizeligheid',
                        type: 'boolean',
                    },
                    {
                        allowOtherOption: true,
                        include: dependsOn('duizeligheid'),
                        isRequired: true,
                        key: 'duizeligheid_soort',
                        label: 'Soort duizeligheid',
                        type: 'radioGroup',
                        valueOptions: duizeligheidOptions,
                    },
                    {
                        include: dependsOn('duizeligheid'),
                        key: 'duizeligheid_toelichting',
                        label: 'Toelichting duizeligheid',
                        type: 'textArea',
                    },
                    {
                        isRequired: true,
                        key: 'oorsuizen',
                        label: 'Oorsuizen',
                        type: 'boolean',
                    },
                    {
                        include: dependsOn('oorsuizen'),
                        isRequired: true,
                        key: 'oorsuizen_zijde',
                        label: 'Zijde oorsuizen',
                        type: 'radioGroup',
                        valueOptions: oorZijdeOptions,
                    },
                    {
                        // TODO: CS-24682 Remove when there is a default schema for monthInput
                        draftSchema: Koi.when('oorsuizen', {
                            is: true,
                            otherwise: Koi.valid(null),
                            then: Koi.koi().date().allow(null),
                        }),
                        finalSchema: Koi.when('oorsuizen', {
                            is: true,
                            otherwise: Koi.valid(null),
                            then: getTodayOrBeforeDateSchema(),
                        }),
                        include: dependsOn('oorsuizen'),
                        isRequired: true,
                        key: 'oorsuizen_begin',
                        label: 'Begin oorsuizen',
                        maxValue: new Date(),
                        type: 'monthInput',
                    },
                    {
                        allowOtherOption: true,
                        include: dependsOn('oorsuizen'),
                        isRequired: true,
                        key: 'oorsuizen_soort',
                        label: 'Soort oorsuizen',
                        type: 'radioGroup',
                        valueOptions: oorsuizenSoortOptions,
                    },
                    {
                        include: dependsOn('oorsuizen'),
                        isRequired: true,
                        key: 'oorsuizen_beschrijving',
                        label: 'Beschrijf geluid',
                        tooltipText: 'Hoog/laag, piep/brom etc.',
                        type: 'textInput',
                    },
                    {
                        include: dependsOn('oorsuizen'),
                        key: 'oorsuizen_toelichting',
                        label: 'Toelichting oorsuizen',
                        type: 'textArea',
                    },
                    {
                        include: dependsOn('verwijs_naar', ({ verwijs_naar }) => verwijs_naar === VerwijsNaar.knoArts),
                        isRequired: true,
                        key: 'oorpijn',
                        label: 'Oorpijn',
                        type: 'boolean',
                    },
                    {
                        include: dependsOn('oorpijn'),
                        isRequired: true,
                        key: 'oorpijn_zijde',
                        label: 'Zijde oorpijn',
                        type: 'radioGroup',
                        valueOptions: oorZijdeOptions,
                    },
                    {
                        // TODO: CS-24682 Remove when there is a default schema for monthInput
                        draftSchema: Koi.when('oorpijn', {
                            is: true,
                            otherwise: Koi.valid(null),
                            then: Koi.koi().date().allow(null),
                        }),
                        finalSchema: Koi.when('oorpijn', {
                            is: true,
                            otherwise: Koi.valid(null),
                            then: getTodayOrBeforeDateSchema(),
                        }),
                        include: dependsOn('oorpijn'),
                        isRequired: true,
                        key: 'oorpijn_begin',
                        label: 'Begin oorpijn',
                        maxValue: new Date(),
                        type: 'monthInput',
                    },
                    {
                        include: dependsOn('oorpijn'),
                        key: 'oorpijn_toelichting',
                        label: 'Toelichting oorpijn',
                        type: 'textArea',
                    },
                    {
                        include: dependsOn('verwijs_naar', ({ verwijs_naar }) => verwijs_naar === VerwijsNaar.knoArts),
                        isRequired: true,
                        key: 'oorontsteking',
                        label: 'Oorontsteking',
                        type: 'boolean',
                    },
                    {
                        include: dependsOn('oorontsteking'),
                        isRequired: true,
                        key: 'oorontsteking_zijde',
                        label: 'Zijde oorontsteking',
                        type: 'radioGroup',
                        valueOptions: oorZijdeOptions,
                    },
                    {
                        include: dependsOn('oorontsteking'),
                        isRequired: true,
                        key: 'oorontsteking_chronisch',
                        label: 'Chronische oorontsteking',
                        type: 'boolean',
                    },
                    {
                        // TODO: CS-24682 Remove when there is a default schema for monthInput
                        draftSchema: Koi.when('oorontsteking', {
                            is: true,
                            otherwise: Koi.valid(null),
                            then: Koi.koi().date().allow(null),
                        }),
                        finalSchema: Koi.when('oorontsteking', {
                            is: true,
                            otherwise: Koi.valid(null),
                            then: getTodayOrBeforeDateSchema(),
                        }),
                        include: dependsOn('oorontsteking'),
                        isRequired: true,
                        key: 'oorontsteking_begin',
                        label: 'Begin oorontsteking',
                        maxValue: new Date(),
                        type: 'monthInput',
                    },
                    {
                        include: dependsOn('oorontsteking'),
                        key: 'oorontsteking_toelichting',
                        label: 'Toelichting oorontsteking',
                        type: 'textArea',
                    },
                    {
                        isRequired: true,
                        key: 'oorsmeer_veel',
                        label: 'Veel oorsmeer',
                        type: 'boolean',
                    },
                    {
                        include: dependsOn('oorsmeer_veel'),
                        isRequired: true,
                        key: 'oorsmeer_zijde',
                        label: 'Zijde oorsmeer',
                        type: 'radioGroup',
                        valueOptions: oorZijdeOptions,
                    },
                    {
                        include: dependsOn('oorsmeer_veel'),
                        isRequired: true,
                        key: 'oorsmeer_regelmatig_reinigen',
                        label: 'Oren regelmatig reinigen noodzakelijk',
                        type: 'boolean',
                    },
                    {
                        include: dependsOn('oorsmeer_veel'),
                        key: 'oorsmeer_toelichting',
                        label: 'Toelichting oorsmeer',
                        type: 'textArea',
                    },
                    {
                        isRequired: true,
                        key: 'overige_klachten',
                        label: 'Overige klachten',
                        type: 'boolean',
                    },
                    {
                        include: dependsOn('overige_klachten'),
                        isRequired: true,
                        key: 'overige_klachten_toelichting',
                        label: 'Toelichting klachten',
                        type: 'textArea',
                    },
                ],
                title: 'Huidige klachten',
            },
            {
                fields: [
                    {
                        include: dependsOn('verwijs_naar', ({ verwijs_naar }) => verwijs_naar === VerwijsNaar.knoArts),
                        isRequired: true,
                        key: 'hoofdtrauma',
                        label: 'Hoofdtrauma',
                        type: 'boolean',
                    },
                    {
                        include: dependsOn('hoofdtrauma'),
                        isRequired: true,
                        key: 'hoofdtrauma_soort',
                        label: 'Soort hoofdtrauma',
                        type: 'textInput',
                    },
                    {
                        // TODO: CS-24682 Remove when there is a default schema for monthInput
                        draftSchema: Koi.when('hoofdtrauma', {
                            is: true,
                            otherwise: Koi.valid(null),
                            then: Koi.koi().date().allow(null),
                        }),
                        finalSchema: Koi.when('hoofdtrauma', {
                            is: true,
                            otherwise: Koi.valid(null),
                            then: getTodayOrBeforeDateSchema(),
                        }),
                        include: dependsOn('hoofdtrauma'),
                        isRequired: true,
                        key: 'hoofdtrauma_begin',
                        label: 'Begin hoofdtrauma',
                        maxValue: new Date(),
                        type: 'monthInput',
                    },
                    {
                        include: dependsOn('hoofdtrauma'),
                        key: 'hoofdtrauma_reeds_bezochte_zorgverlener',
                        label: 'Reeds bezochte zorgverlener(s)',
                        type: 'textInput',
                    },
                    {
                        include: dependsOn('hoofdtrauma'),
                        isRequired: true,
                        key: 'hoofdtrauma_momenteel_klachten',
                        label: 'Momenteel klachten van hoofdtrauma',
                        type: 'boolean',
                    },
                    {
                        include: dependsOn('hoofdtrauma_momenteel_klachten'),
                        isRequired: true,
                        key: 'hoofdtrauma_momenteel_klachten_toelichting',
                        label: 'Toelichting klachten',
                        type: 'textArea',
                    },
                    {
                        include: dependsOn('hoofdtrauma'),
                        key: 'hoofdtrauma_toelichting',
                        label: 'Toelichting hoofdtrauma',
                        type: 'textArea',
                    },
                    {
                        isRequired: true,
                        key: 'overmatige_blootstelling_aan_lawaai',
                        label: 'Overmatige blootstelling aan lawaai',
                        type: 'boolean',
                    },
                    {
                        include: dependsOn('overmatige_blootstelling_aan_lawaai'),
                        isRequired: true,
                        key: 'overmatige_blootstelling_aan_lawaai_oorzaak',
                        label: 'Oorzaak',
                        tooltipText:
                            'Geef aan wat de oorzaak is (bijv. werk gerelateerd: vrachtwagen chauffeur en altijd met raam open gereden).',
                        type: 'textArea',
                    },
                    {
                        include: dependsOn('overmatige_blootstelling_aan_lawaai'),
                        isRequired: true,
                        key: 'overmatige_blootstelling_aan_lawaai_periode',
                        label: 'Periode',
                        tooltipText:
                            'Geef aan in welk jaar/jaren en hoelang patiënt overmatig is blootgesteld aan lawaai.',
                        type: 'textArea',
                    },
                    {
                        include: dependsOn('overmatige_blootstelling_aan_lawaai'),
                        key: 'overmatige_blootstelling_aan_lawaai_toelichting',
                        label: 'Toelichting overmatige blootstelling aan lawaai',
                        type: 'textArea',
                    },
                    {
                        isRequired: true,
                        key: 'ooroperatie',
                        label: 'Ooroperatie',
                        type: 'boolean',
                    },
                    {
                        include: dependsOn('ooroperatie'),
                        isRequired: true,
                        key: 'ooroperatie_zijde',
                        label: 'Zijde ooroperatie',
                        type: 'radioGroup',
                        valueOptions: oorZijdeOptions,
                    },
                    {
                        // TODO: CS-24682 Remove when there is a default schema for monthInput
                        draftSchema: Koi.when('ooroperatie', {
                            is: true,
                            otherwise: Koi.valid(null),
                            then: Koi.koi().date().allow(null),
                        }),
                        finalSchema: Koi.when('ooroperatie', {
                            is: true,
                            otherwise: Koi.valid(null),
                            then: getTodayOrBeforeDateSchema(),
                        }),
                        include: dependsOn('ooroperatie'),
                        isRequired: true,
                        key: 'ooroperatie_datum',
                        label: 'Datum ooroperatie',
                        maxValue: new Date(),
                        type: 'monthInput',
                    },
                    {
                        include: dependsOn('ooroperatie'),
                        isRequired: true,
                        key: 'ooroperatie_type',
                        label: 'Type operatie',
                        type: 'textInput',
                    },
                    {
                        include: dependsOn('ooroperatie'),
                        isRequired: true,
                        key: 'ooroperatie_complicaties',
                        label: 'Complicaties',
                        type: 'boolean',
                    },
                    {
                        include: dependsOn('ooroperatie_complicaties'),
                        isRequired: true,
                        key: 'ooroperatie_complicaties_toelichting',
                        label: 'Toelichting complicaties',
                        type: 'textArea',
                    },
                    {
                        include: dependsOn('ooroperatie_complicaties'),
                        isRequired: true,
                        key: 'ooroperatie_complicaties_momenteel_klachten',
                        label: 'Momenteel klachten door complicaties ooroperatie',
                        type: 'boolean',
                    },
                    {
                        include: dependsOn('ooroperatie_complicaties_momenteel_klachten'),
                        isRequired: true,
                        key: 'ooroperatie_complicaties_momenteel_klachten_toelichting',
                        label: 'Toelichting klachten',
                        type: 'textArea',
                    },
                    {
                        include: dependsOn('ooroperatie'),
                        key: 'ooroperatie_toelichting',
                        label: 'Toelichting ooroperatie',
                        type: 'textArea',
                    },
                    {
                        include: dependsOn('verwijs_naar', ({ verwijs_naar }) => verwijs_naar === VerwijsNaar.knoArts),
                        isRequired: true,
                        key: 'hersenvliesontsteking',
                        label: 'Hersenvliesontsteking',
                        type: 'boolean',
                    },
                    {
                        include: dependsOn('hersenvliesontsteking'),
                        isRequired: true,
                        key: 'hersenvliesontsteking_wanneer',
                        label: 'Wanneer hersenvliesontsteking',
                        type: 'textArea',
                    },
                    {
                        include: dependsOn('hersenvliesontsteking'),
                        key: 'hersenvliesontsteking_reeds_bezochte_zorgverlener',
                        label: 'Reeds bezochte zorgverlener(s)',
                        type: 'textInput',
                    },
                    {
                        include: dependsOn('hersenvliesontsteking'),
                        isRequired: true,
                        key: 'hersenvliesontsteking_momenteel_klachten',
                        label: 'Momenteel klachten van hersenvliesontsteking',
                        type: 'boolean',
                    },
                    {
                        include: dependsOn('hersenvliesontsteking_momenteel_klachten'),
                        isRequired: true,
                        key: 'hersenvliesontsteking_momenteel_klachten_toelichting',
                        label: 'Toelichting klachten',
                        type: 'textArea',
                    },
                    {
                        include: dependsOn('hersenvliesontsteking'),
                        key: 'hersenvliesontsteking_toelichting',
                        label: 'Toelichting hersenvliesontsteking',
                        type: 'textArea',
                    },
                    {
                        isRequired: true,
                        key: 'slechthorendheid_in_familie',
                        label: 'Slechthorendheid in familie',
                        type: 'boolean',
                    },
                    {
                        include: dependsOn('slechthorendheid_in_familie'),
                        key: 'slechthorendheid_in_familie_toelichting',
                        label: 'Toelichting slechthorendheid in familie',
                        type: 'textArea',
                    },
                    {
                        isRequired: true,
                        key: 'andere_relevante_voorgeschiedenis',
                        label: 'Andere relevante voorgeschiedenis',
                        type: 'boolean',
                    },
                    {
                        include: dependsOn('andere_relevante_voorgeschiedenis'),
                        isRequired: true,
                        key: 'andere_relevante_voorgeschiedenis_toelichting',
                        label: 'Toelichting',
                        type: 'textArea',
                    },
                ],
                title: 'Relevante voorgeschiedenis',
            },
        ],
        title: 'Anamnese',
    },
    {
        alertText: 'Voeg geen bijlages toe die ouder zijn dan 6 maanden',
        fields: [
            {
                category: AssetCategory.audiometrie,
                draftSchema: getProcessActivityAssetsSchema({ category: AssetCategory.audiometrie }),
                finalSchema: getProcessActivityAssetsSchema({ category: AssetCategory.audiometrie, min: 1 }),
                isRequired: true,
                key: 'audiometrie',
                label: 'Audiometrie',
                type: 'assets',
            },
            {
                category: AssetCategory.trommelvliesfoto,
                draftSchema: Koi.when('verwijs_naar', {
                    is: VerwijsNaar.knoArts,
                    otherwise: getEmptyProcessActivityAssetCategorySchema(AssetCategory.trommelvliesfoto),
                    then: getProcessActivityAssetsSchema({ category: AssetCategory.trommelvliesfoto }),
                }),
                finalSchema: Koi.when('verwijs_naar', {
                    is: VerwijsNaar.knoArts,
                    otherwise: getEmptyProcessActivityAssetCategorySchema(AssetCategory.trommelvliesfoto),
                    then: getProcessActivityAssetsSchema({ category: AssetCategory.trommelvliesfoto, min: 1 }),
                }),
                include: dependsOn('verwijs_naar', ({ verwijs_naar }) => verwijs_naar === VerwijsNaar.knoArts),
                isRequired: true,
                key: 'trommelvliesfoto',
                label: 'Trommelvliesfoto',
                type: 'assets',
            },
            {
                category: AssetCategory.huidigeHoortoestelinstellingen,
                draftSchema: Koi.alternatives().try(
                    Koi.when('verwijs_naar', {
                        is: VerwijsNaar.audioloog,
                        otherwise: getEmptyProcessActivityAssetCategorySchema(
                            AssetCategory.huidigeHoortoestelinstellingen
                        ),
                        then: Koi.forbidden(),
                    }),
                    Koi.when('reden_verwijzing', {
                        is: Koi.valid(
                            RedenVerwijzingAudioloog.hoortoestelAdviesNaProef,
                            RedenVerwijzingAudioloog.adviesOverigeHulpmiddelen,
                            'Anders'
                        ),
                        otherwise: getEmptyProcessActivityAssetCategorySchema(
                            AssetCategory.huidigeHoortoestelinstellingen
                        ),
                        then: getProcessActivityAssetsSchema({
                            category: AssetCategory.huidigeHoortoestelinstellingen,
                        }),
                    })
                ),
                finalSchema: Koi.alternatives().try(
                    Koi.when('verwijs_naar', {
                        is: VerwijsNaar.audioloog,
                        otherwise: getEmptyProcessActivityAssetCategorySchema(
                            AssetCategory.huidigeHoortoestelinstellingen
                        ),
                        then: Koi.forbidden(),
                    }),
                    Koi.when('reden_verwijzing', {
                        otherwise: getEmptyProcessActivityAssetCategorySchema(
                            AssetCategory.huidigeHoortoestelinstellingen
                        ),
                        switch: [
                            {
                                is: Koi.valid(
                                    RedenVerwijzingAudioloog.hoortoestelAdviesNaProef,
                                    RedenVerwijzingAudioloog.adviesOverigeHulpmiddelen
                                ),
                                then: getProcessActivityAssetsSchema({
                                    category: AssetCategory.huidigeHoortoestelinstellingen,
                                    min: 1,
                                }),
                            },
                            {
                                is: Koi.valid('Anders'),
                                then: getProcessActivityAssetsSchema({
                                    category: AssetCategory.huidigeHoortoestelinstellingen,
                                }),
                            },
                        ],
                    })
                ),
                include: dependsOn(
                    ['verwijs_naar', 'reden_verwijzing'],
                    ({ reden_verwijzing, verwijs_naar }) =>
                        verwijs_naar === VerwijsNaar.audioloog &&
                        reden_verwijzing !== null &&
                        [
                            RedenVerwijzingAudioloog.hoortoestelAdviesNaProef,
                            RedenVerwijzingAudioloog.adviesOverigeHulpmiddelen,
                            'Anders',
                        ].includes(reden_verwijzing)
                ),
                isRequired: dependsOn(['reden_verwijzing'], ({ reden_verwijzing }) => reden_verwijzing !== 'Anders'),
                key: 'huidige_hoortoestelinstellingen',
                label: 'Huidige hoortoestelinstellingen',
                type: 'assets',
            },
            {
                category: AssetCategory.remMeting,
                draftSchema: Koi.alternatives().try(
                    Koi.when('verwijs_naar', {
                        is: VerwijsNaar.audioloog,
                        otherwise: getEmptyProcessActivityAssetCategorySchema(AssetCategory.remMeting),
                        then: Koi.forbidden(),
                    }),
                    Koi.when('reden_verwijzing', {
                        is: Koi.valid(
                            RedenVerwijzingAudioloog.hoortoestelAdviesNaProef,
                            RedenVerwijzingAudioloog.adviesOverigeHulpmiddelen,
                            'Anders'
                        ),
                        otherwise: getEmptyProcessActivityAssetCategorySchema(AssetCategory.remMeting),
                        then: getProcessActivityAssetsSchema({
                            category: AssetCategory.remMeting,
                        }),
                    })
                ),
                finalSchema: Koi.alternatives().try(
                    Koi.when('verwijs_naar', {
                        is: VerwijsNaar.audioloog,
                        otherwise: getEmptyProcessActivityAssetCategorySchema(AssetCategory.remMeting),
                        then: Koi.forbidden(),
                    }),
                    Koi.when('reden_verwijzing', {
                        otherwise: getEmptyProcessActivityAssetCategorySchema(AssetCategory.remMeting),
                        switch: [
                            {
                                is: Koi.valid(
                                    RedenVerwijzingAudioloog.hoortoestelAdviesNaProef,
                                    RedenVerwijzingAudioloog.adviesOverigeHulpmiddelen
                                ),
                                then: getProcessActivityAssetsSchema({ category: AssetCategory.remMeting, min: 1 }),
                            },
                            {
                                is: Koi.valid('Anders'),
                                then: getProcessActivityAssetsSchema({ category: AssetCategory.remMeting }),
                            },
                        ],
                    })
                ),
                include: dependsOn(
                    ['verwijs_naar', 'reden_verwijzing'],
                    ({ reden_verwijzing, verwijs_naar }) =>
                        verwijs_naar === VerwijsNaar.audioloog &&
                        reden_verwijzing !== null &&
                        [
                            RedenVerwijzingAudioloog.hoortoestelAdviesNaProef,
                            RedenVerwijzingAudioloog.adviesOverigeHulpmiddelen,
                            'Anders',
                        ].includes(reden_verwijzing)
                ),
                isRequired: dependsOn(['reden_verwijzing'], ({ reden_verwijzing }) => reden_verwijzing !== 'Anders'),
                key: 'rem_meting_en_vrije_veld_spraakaudiometrie',
                label: 'REM-meting en vrije veld spraakaudiometrie',
                tooltipText:
                    'REM meting & vrije veld spraakaudiometrie bij huidige instellingen (minimaal mono-auraal bij 3 intensiteiten)',
                type: 'assets',
            },
        ],
        title: 'Onderzoeken',
    },
    {
        fields: [
            {
                key: 'vraag_aan_specialist',
                label: 'Vraag aan specialist',
                type: 'textArea',
            },
        ],
        title: 'Overige vragen',
    },
];
